
import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIntl } from "react-intl";

import CustomTable from 'components/table';
import { ActionMenu, ConfirmationButton, CustomButton } from 'components';
import { masterPaginationServices, tableInitialState, } from 'utils/constants';
import { getTableParams } from 'utils/global';
import { RootReducerState } from 'utils/types';
import { getMasterPaginationData } from 'redux/actions';
import PageLayout from 'layouts/PageLayout';
import Form from './Form';
import { services } from 'redux/services';
import { useToastMessage } from 'hooks';
import { faqColumns } from 'utils/constants/tableColumn/column';
import { Edit, Delete } from '@mui/icons-material';

const Index: React.FC = () => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const { toastMessage } = useToastMessage();

    const [tableState, setTableState] = useState(tableInitialState);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [drawer, setDrawer] = useState(false);
    const [createMode, setCreateMode] = useState(true);
    const [selectedRow, setSelectedRow] = useState({});

    const { userData, loading } = useSelector(
        ({ masterPaginationReducer }: RootReducerState) => ({
            userData: masterPaginationReducer[masterPaginationServices.faq].data,
            loading: masterPaginationReducer[masterPaginationServices.faq].loading,
        }),
        shallowEqual
    );

    const { items, count } = userData;

    useEffect(() => {
        onApiCall();
    }, [tableState]);

    function onApiCall() {
        const params = getTableParams(tableState);
        dispatch(getMasterPaginationData(masterPaginationServices.faq, params));
    }

    const columnAction = [
        {
            label: formatMessage({ id: "action" }),
            name: "",
            options: {
                customBodyRender: (_: any, tableMeta: any) => {
                    const rowData = items[tableMeta.rowIndex];
                    return (
                        <ActionMenu>
                            <CustomButton
                                color='info'
                                size='small'
                                label="Edit"
                                variant='outlined'
                                onClick={() => handleRowClick(rowData)}
                                startIcon={<Edit />}
                            />
                            <ConfirmationButton
                                color='error'
                                size='small'
                                label="Delete"
                                variant='outlined'
                                onConfirm={() => onDeleteData(rowData)}
                                title={formatMessage({ id: "delete-title" })}
                                subTitle={formatMessage({ id: "delete-confirmation" })}
                                startIcon={<Delete />}
                            />
                        </ActionMenu>
                    )
                }
            }
        }]

    function handleRowClick(rowData: any) {
        setSelectedRow(rowData);
        setDrawer(true);
        setCreateMode(false);
    }

    function handleCreate() {
        setSelectedRow({});
        setDrawer(true);
        setCreateMode(true);
    }

    function onDeleteData(data: any) {
        const params = { id: data.id };
        setDeleteLoading(true);
        services.deleteSurvey(params)
            .then((res: any) => {
                setDeleteLoading(false);
                onApiCall();
                toastMessage(formatMessage({ id: "delete-message" }));
            })
            .catch((err) => {
                setDeleteLoading(false);
                toastMessage(JSON.stringify(err?.data?.message) || formatMessage({ id: "opps-error-message" }), 'error');
            })
    }

    return (
        <PageLayout title={formatMessage({ id: "faq" })} onCreate={handleCreate}>
            <CustomTable
                columns={[...faqColumns(formatMessage), ...columnAction]}
                tableData={items}
                tableState={tableState}
                itemsCount={count}
                setTableState={setTableState}
                loading={loading || deleteLoading}
            />

            {drawer && (
                <Form
                    refreshData={onApiCall}
                    open={drawer}
                    closeModal={() => setDrawer(false)}
                    formMode={createMode ? 'create' : 'update'}
                    selectedData={selectedRow}
                    headerText={createMode ? formatMessage({ id: "create-faq" }) : formatMessage({ id: "update-faq" })}
                />
            )}
        </PageLayout>
    )
}

export default Index;
